import { Option } from '@code/innovance-react-ui-kit';

import { AddressData, DictionaryLine } from 'src/front/api/generated';

export const formatNumeric = (value: string): string => value.replace(/[^0-9]*/g, '');

export const formatAlphaNumeric = (value: string): string =>
  value.replace(/[^a-zA-Z0-9]*$/g, '').toUpperCase();

export const formatEmail = (value: string): string =>
  value.replace(/[^a-zA-Z0-9.@!#$%&'*/=?^_+-`{|}~]/g, '').toLocaleLowerCase();

// TODO(mertohancan): find a generic name
export const formatEmbossName = (value: string): string =>
  value.replace(/[^a-zA-Z., -]/g, '').toUpperCase();

export const formatAlphaNumericCyrillicPlusDashAndBlank = (value: string): string =>
  value.replace(/[^a-zA-ZığüşöçİĞÜŞÖÇа-яА-Я0-9- ]*$/g, '').toUpperCase();
export const formatAlphaNumericCyrillicPlusDashAndBlankAndDot = (value: string): string =>
  value.replace(/^[.]$|[^a-zA-ZığüşöçİĞÜŞÖÇа-яА-Я0-9- .]*$/g, '').toUpperCase();

export const formatAlphaNumericCyrillicPlusBlank = (value: string): string =>
  value.replace(/[^a-zA-ZığüşöçİĞÜŞÖÇа-яА-Я0-9 ]*$/g, '').toUpperCase();

export const formatBlank = (value: string): string => value.replace(/ /g, '');

export const formatAlphaNumericCyrillicSpecialCharacters = (value: string): string =>
  value.replace(/[^a-zA-ZığüşöçİĞÜŞÖÇа-яА-Я0-9-'Ёё ]/g, '').toUpperCase();
export const formatAlphaNumericCyrillicSpecialCharactersWithDot = (value: string): string =>
  value.replace(/^[.]$|[^a-zA-ZığüşöçİĞÜŞÖÇа-яА-Я0-9-'Ёё .]/g, '').toUpperCase();

export const formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes = (value: string): string =>
  value.replace(/[^a-zA-ZığüşöçİĞÜŞÖÇа-яА-Я0-9-Ёё ]/g, '').toUpperCase();
export const formatFIONonResident = (value: string): string =>
  value.replace(/[^ёЁа-яА-Я- '().]/g, '').toUpperCase();

/**
 * Проверяет наличие латиницы в ФИО (задача #46997).
 * Римские цифры I, V, X (латинские и кириллическая Х) в конце поля
 * (после пробела) разрешены и не считаются латиницей:
 * «Грозный Иван IV», «Екатерина II», «Иван ХIХ» — не латиница.
 */
export const containsLatinFIO = (value: string | null | undefined): boolean =>
  /[a-zA-ZığüşöçİĞÜŞÖÇ]/.test((value ?? '').trim().replace(/( [IVXivxХх]+)+$/, ''));
export const formatCyrillic = (value: string): string =>
  value.replace(/[^ёЁа-яА-Я]/g, '').toUpperCase();

export const formatFreeText = (value: string): string => value.replace(/[“”«»]/g, '').toUpperCase();

export const formatSnils = (value: string): string =>
  value.replace(/[^0-9]*/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1-$2-$3 $4');

export const formatMaskedText = (value: string, maskFormat?: string): string => {
  if (!maskFormat) return value;
  const clearedValue = value
    .split('')
    .filter((ch) => maskFormat.includes('X') && maskFormat.replace(/X/g, '').indexOf(ch) === -1)
    .join('');
  let index = 0;
  return maskFormat
    .split('')
    .map((ch) => {
      if (ch === 'X') {
        // eslint-disable-next-line no-plusplus
        return clearedValue[index++] || '';
      }
      return ch;
    })
    .join('');
};

export const optionFormat = (parameter: DictionaryLine): Option => ({
  value: parameter.code,
  label: parameter.value,
});

export const dictionaryLineFormat = (option: Option | null): DictionaryLine => ({
  code: option?.value || '',
  value: option?.label || '',
});

export const formatAmount = (amount?: number): string =>
  (amount?.toString() || '').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');

type Suggestion = {
  country: Option | null;
  region: Option | null;
  city: string;
  district: Option | null;
  streetType: Option | null;
  street: string;
  houseNumber: string;
  buildingInfo: string;
  zipCode: string;
  flatNumber: string;
};

export const formatAddressData = (suggestion: Suggestion): AddressData => {
  const { country, region, district, streetType, ...rest } = suggestion;
  return {
    ...rest,
    country: dictionaryLineFormat(country),
    region: dictionaryLineFormat(region),
    district: dictionaryLineFormat(district),
    streetType: dictionaryLineFormat(streetType),
  };
};

export const formatAddressSuggestion = (addressData: AddressData): Suggestion => {
  const { country, region, district, streetType } = addressData;
  return {
    zipCode: addressData.zipCode || '',
    buildingInfo: addressData.buildingInfo || '',
    houseNumber: addressData.houseNumber || '',
    flatNumber: addressData.flatNumber || '',
    city: addressData.city || '',
    street: addressData.street || '',
    country: country ? optionFormat(country) : null,
    region: region ? optionFormat(region) : null,
    district: district ? optionFormat(district) : null,
    streetType: streetType ? optionFormat(streetType) : null,
  };
};

export const splitCustomerName = (customerName: string, customerSecondName?: string): string => {
  if (customerName.split(' ').length === 2) {
    const [name, surname] = customerName.split(' ');
    return `${surname} ${name} ${customerSecondName ? ` ${customerSecondName}` : ''}`;
  }
  const [name, middleName, surname] = customerName.split(' ');
  return `${surname} ${name} ${middleName} ${customerSecondName ? ` ${customerSecondName}` : ''}`;
};
